import Cookies from 'js-cookie';
import { getDeploymentConfig } from '../services/config';

/**
 * Cookie names for storing authentication tokens
 */
export const COOKIE_NAMES = {
  ACCESS_TOKEN: 'qodo_access_token',
  REFRESH_TOKEN: 'qodo_refresh_token',
  TOKEN_EXPIRY: 'qodo_token_expiry',
  ID_TOKEN: 'qodo_id_token',
} as const;

/**
 * Service subdomains that are safe to strip to derive a shared cookie domain
 * when no explicit `parent_domain` is configured. These prefix Qodo-owned hosts
 * whose remaining labels are always a private (non-public-suffix) domain.
 */
const KNOWN_SERVICE_SUBDOMAINS = ['app', 'portal', 'qodo-platform'] as const;

/**
 * Extracts the shared parent domain used for cross-subdomain auth cookies.
 *
 * Prefers the deployment's configured `parent_domain`. When that is absent it
 * falls back to stripping a *known* service subdomain (e.g. `app.`) — a generic
 * "strip the first label" fallback is intentionally avoided because it can yield
 * a public-suffix cookie domain (e.g. `.web.app` for `x.web.app`) that browsers
 * silently reject, which would break the 409 cross-subdomain token handoff.
 *
 * @returns The shared parent domain with leading dot, or undefined if none applies
 * @example
 * // parent_domain configured as 'qodo.ai'
 * getSharedDomain() // '.qodo.ai'
 *
 * // On app.qodo.ai with no parent_domain configured
 * getSharedDomain() // '.qodo.ai'
 *
 * // On staging-new-21509.web.app with no parent_domain configured
 * getSharedDomain() // undefined (avoids invalid '.web.app')
 *
 * // On localhost
 * getSharedDomain() // undefined (cookies won't work on localhost)
 */
export const getSharedDomain = (): string | undefined => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }

  const parentDomain = getDeploymentConfig().parent_domain;

  if (parentDomain) {
    return `.${parentDomain}`;
  }

  const [firstLabel, ...rest] = hostname.split('.');
  const isKnownServiceSubdomain = (
    KNOWN_SERVICE_SUBDOMAINS as readonly string[]
  ).includes(firstLabel);

  // Require at least two remaining labels so we never strip down to a bare TLD.
  if (isKnownServiceSubdomain && rest.length >= 2) {
    return `.${rest.join('.')}`;
  }

  return undefined;
};

/**
 * Saves the access token, refresh token, and token expiry to a shared domain cookie.
 * This enables the tokens to be accessible across different subdomains
 * during cross-tenant redirects.
 *
 * Should be called BEFORE performing a 409 redirect to ensure the tokens
 * are available on the destination subdomain.
 *
 * @param accessToken - The OAuth access token to save
 * @param refreshToken - The OAuth refresh token to save
 * @param tokenExpiry - The token expiry timestamp in milliseconds (optional)
 * @param idToken - The OIDC ID token (optional) — carried so the destination
 * portal can send it as `id_token_hint` on sign-out; without it end_session
 * can't revoke provider-side credentials
 * @returns true if cookies were set successfully, false otherwise
 *
 * @example
 * const success = saveAuthTokensToCookie('eyJhbGc...', 'refresh_xyz', '1234567890000');
 * if (success) {
 *   window.location.href = redirectUrl;
 * }
 */
export const saveAuthTokensToCookie = (
  accessToken: string,
  refreshToken: string,
  tokenExpiry?: string,
  idToken?: string,
): boolean => {
  const sharedDomain = getSharedDomain();

  if (!sharedDomain) {
    console.warn(
      'Cannot save access token to cookie: shared domain not available (localhost or unknown domain)',
    );

    return false;
  }

  try {
    const cookieOptions = buildCookieOptions(sharedDomain, tokenExpiry);

    saveTokenCookies(
      accessToken,
      refreshToken,
      tokenExpiry,
      idToken,
      cookieOptions,
    );

    if (!isTokenCookieSaved(accessToken, refreshToken)) {
      console.error('Token cookie verification failed after save.');

      return false;
    }

    return true;
  } catch (error) {
    console.error('Failed to save auth tokens to cookie:', error);

    return false;
  }
};

const buildCookieOptions = (
  sharedDomain: string,
  tokenExpiry?: string,
): Cookies.CookieAttributes => {
  const ONE_HOUR_IN_MS = 60 * 60 * 1000;
  const expiryDate = tokenExpiry
    ? new Date(parseInt(tokenExpiry))
    : new Date(Date.now() + ONE_HOUR_IN_MS);

  return {
    domain: sharedDomain,
    sameSite: 'strict',
    secure: true,
    expires: expiryDate,
  };
};

const saveTokenCookies = (
  accessToken: string,
  refreshToken: string,
  tokenExpiry: string | undefined,
  idToken: string | undefined,
  cookieOptions: Cookies.CookieAttributes,
): void => {
  Cookies.set(COOKIE_NAMES.ACCESS_TOKEN, accessToken, cookieOptions);
  Cookies.set(COOKIE_NAMES.REFRESH_TOKEN, refreshToken, cookieOptions);

  if (tokenExpiry) {
    Cookies.set(COOKIE_NAMES.TOKEN_EXPIRY, tokenExpiry, cookieOptions);
  }

  if (idToken) {
    Cookies.set(COOKIE_NAMES.ID_TOKEN, idToken, cookieOptions);
  }
};

const isTokenCookieSaved = (
  expectedAccessToken: string,
  expectedRefreshToken: string,
): boolean => {
  const savedAccessToken = Cookies.get(COOKIE_NAMES.ACCESS_TOKEN);
  const savedRefreshToken = Cookies.get(COOKIE_NAMES.REFRESH_TOKEN);

  if (savedAccessToken !== expectedAccessToken) {
    console.error(
      'Cookie verification failed: saved access token does not match expected value',
    );

    return false;
  }

  if (savedRefreshToken !== expectedRefreshToken) {
    console.error(
      'Cookie verification failed: saved refresh token does not match expected value',
    );

    return false;
  }

  return true;
};

/**
 * Retrieves the access token, refresh token, and token expiry from the shared domain cookie.
 * Should be called on page load if localStorage is empty,
 * to check if tokens are available from a previous redirect.
 *
 * @returns Object containing accessToken, refreshToken, and tokenExpiry, or undefined if not found
 *
 * @example
 * const tokenData = getAuthTokensFromCookie();
 * if (tokenData) {
 *   // Restore to localStorage
 *   localStorage.setItem('accessToken', tokenData.accessToken);
 *   localStorage.setItem('refreshToken', tokenData.refreshToken);
 * }
 */
export const getAuthTokensFromCookie = ():
  | {
      accessToken: string;
      refreshToken: string;
      tokenExpiry?: string;
      idToken?: string;
    }
  | undefined => {
  try {
    const accessToken = Cookies.get(COOKIE_NAMES.ACCESS_TOKEN);
    const refreshToken = Cookies.get(COOKIE_NAMES.REFRESH_TOKEN);

    if (!accessToken || !refreshToken) {
      return undefined;
    }

    const tokenExpiry = Cookies.get(COOKIE_NAMES.TOKEN_EXPIRY);
    const idToken = Cookies.get(COOKIE_NAMES.ID_TOKEN);

    return {
      accessToken,
      refreshToken,
      tokenExpiry,
      idToken,
    };
  } catch (error) {
    console.error('Failed to retrieve auth tokens from cookie:', error);

    return undefined;
  }
};

/**
 * Presence check for EITHER auth cookie (unlike getAuthTokensFromCookie, which
 * requires both). Lets the restore path clean up a partial/orphaned fragment via
 * removeAccessTokenCookie's finally block. Returns true on read error so cleanup
 * routes through the restore try/catch rather than being silently skipped.
 *
 * @returns True if at least one auth-token cookie is present
 */
export const hasAuthTokenCookie = (): boolean => {
  try {
    return (
      Cookies.get(COOKIE_NAMES.ACCESS_TOKEN) !== undefined ||
      Cookies.get(COOKIE_NAMES.REFRESH_TOKEN) !== undefined
    );
  } catch {
    return true;
  }
};

/**
 * All cookie domains the current host can address: host-only (no domain
 * attribute), this deployment's shared domain, and every ancestor domain of
 * the hostname down to two labels. The auth cookies may have been set by a
 * DIFFERENT portal — e.g. the MT writes them on `.qodo.ai` before a
 * cross-tenant redirect to `app.<tenant>.st.qodo.ai`, whose own shared domain
 * resolves to `.<tenant>.st.qodo.ai` — and cookie deletion only applies on an
 * exact domain match, so removal must sweep every candidate rather than trust
 * the locally-computed value. Public-suffix candidates are harmless: browsers
 * silently reject the delete-cookie write.
 */
const getCandidateCookieDomains = (): (string | undefined)[] => {
  const hostname = window.location.hostname;
  const candidates = new Set<string | undefined>([undefined]);

  const sharedDomain = getSharedDomain();

  if (sharedDomain) {
    candidates.add(sharedDomain);
  }

  const isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

  if (hostname === 'localhost' || isIpAddress) {
    return [...candidates];
  }

  const labels = hostname.split('.');

  for (let start = 0; start <= labels.length - 2; start++) {
    candidates.add(`.${labels.slice(start).join('.')}`);
  }

  return [...candidates];
};

/**
 * Removes all authentication cookies on every domain the current host can
 * address (see getCandidateCookieDomains — the setting portal's domain may
 * differ from this one's). Should be called after successfully restoring the
 * token to localStorage, or during sign out to ensure complete cleanup.
 */
export const removeAccessTokenCookie = (): void => {
  try {
    const candidateDomains = getCandidateCookieDomains();

    Object.values(COOKIE_NAMES).forEach((cookieName) => {
      candidateDomains.forEach((domain) => {
        Cookies.remove(cookieName, {
          ...(domain && { domain }),
          sameSite: 'strict',
          secure: true,
        });
      });
    });

    console.info('Authentication cookies removed');
  } catch (error) {
    console.error('Failed to remove authentication cookies:', error);
  }
};
